import { useMemo } from 'react';
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  GeoJSON,
  Tooltip,
} from 'react-leaflet';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import { MAP_TILE_ATTRIBUTION, MAP_TILE_URL } from '@src/constants/mapTiles';

const STATUS_STYLE = {
  add: { color: '#2e7d32', label: 'Added vs live targeting' },
  keep: { color: '#90a4ae', label: 'Unchanged' },
  remove: { color: '#d32f2f', label: 'Removed' },
  selected: { color: '#1e88e5', label: 'In selection' },
};

function normalizeStatus(status) {
  return STATUS_STYLE[status] ? status : 'selected';
}

// ZipCodeBoundary.geojson may hold a bare geometry, a Feature, or a whole
// FeatureCollection — normalize everything to Features tagged with our props.
function toFeatures(item) {
  const raw = item.geometry;
  if (!raw || !raw.type) {
    return [];
  }
  const properties = {
    zip: item.zip,
    territory: item.territory_name || '',
  };
  if (raw.type === 'FeatureCollection') {
    return (raw.features || []).map((feature) => ({
      ...feature,
      properties: { ...(feature.properties || {}), ...properties },
    }));
  }
  if (raw.type === 'Feature') {
    return [{ ...raw, properties: { ...(raw.properties || {}), ...properties } }];
  }
  return [{ type: 'Feature', geometry: raw, properties }];
}

// react-leaflet's GeoJSON layer ignores data-prop changes, so the key must
// change whenever the underlying ZIP set does.
function zipSignature(features) {
  let hash = 5381;
  features.forEach((feature) => {
    const zip = feature.properties?.zip || '';
    for (let i = 0; i < zip.length; i += 1) {
      // eslint-disable-next-line no-bitwise
      hash = ((hash * 33) ^ zip.charCodeAt(i)) >>> 0;
    }
  });
  return `${features.length}-${hash.toString(36)}`;
}

function LegendDot({ color }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: color,
        border: '1.5px solid #fff',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
        marginRight: 0.75,
        verticalAlign: 'middle',
      }}
    />
  );
}

/**
 * ZIP coverage map of the current selection. ZIPs with a stored boundary
 * render as real polygons; ZIPs without one fall back to centroid dots.
 * When a live-targeting diff is available (exactly one ad set selected),
 * everything is colored added / unchanged / removed.
 */
export default function SelectionCoverageMap({
  items = [],
  loading = false,
  truncated = false,
  hasDiff = false,
  height = 320,
}) {
  const bounds = useMemo(() => {
    if (!items.length) return null;
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;
    items.forEach((item) => {
      if (item.latitude < minLat) minLat = item.latitude;
      if (item.latitude > maxLat) maxLat = item.latitude;
      if (item.longitude < minLng) minLng = item.longitude;
      if (item.longitude > maxLng) maxLng = item.longitude;
    });
    const pad = 0.05;
    return [
      [minLat - pad, minLng - pad],
      [maxLat + pad, maxLng + pad],
    ];
  }, [items]);

  const counts = useMemo(() => {
    const result = { add: 0, keep: 0, remove: 0, selected: 0 };
    items.forEach((item) => {
      result[normalizeStatus(item.status)] += 1;
    });
    return result;
  }, [items]);

  // One FeatureCollection per status keeps the polygon layer count at ≤4
  // instead of one React component per ZIP.
  const { polygonLayers, dotItems } = useMemo(() => {
    const featuresByStatus = {};
    const dots = [];
    items.forEach((item) => {
      const features = toFeatures(item);
      if (!features.length) {
        dots.push(item);
        return;
      }
      const status = normalizeStatus(item.status);
      if (!featuresByStatus[status]) {
        featuresByStatus[status] = [];
      }
      featuresByStatus[status].push(...features);
    });
    const layers = Object.entries(featuresByStatus).map(([status, features]) => ({
      status,
      key: `${status}-${zipSignature(features)}`,
      data: { type: 'FeatureCollection', features },
    }));
    return { polygonLayers: layers, dotItems: dots };
  }, [items]);

  if (loading) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (!items.length || !bounds) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          No ZIPs in the current selection to map.
        </Typography>
      </Box>
    );
  }

  const legendEntries = hasDiff ? ['add', 'keep', 'remove'] : ['selected'];

  return (
    <Box>
      <Box
        sx={{
          height,
          borderRadius: 1,
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
        }}
      >
        <MapContainer
          bounds={bounds}
          boundsOptions={{ padding: [12, 12] }}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={false}
          attributionControl={false}
          preferCanvas
          maxZoom={13}
        >
          <TileLayer
            url={MAP_TILE_URL}
            maxZoom={13}
            keepBuffer={1}
            updateWhenIdle
            updateWhenZooming={false}
            attribution={MAP_TILE_ATTRIBUTION}
          />
          {polygonLayers.map((layer) => {
            const { color } = STATUS_STYLE[layer.status];
            return (
              <GeoJSON
                key={layer.key}
                data={layer.data}
                style={() => ({
                  color,
                  weight: 1,
                  fillColor: color,
                  fillOpacity: 0.4,
                })}
                onEachFeature={(feature, mapLayer) => {
                  const zip = feature.properties?.zip || '';
                  const territory = feature.properties?.territory || '';
                  mapLayer.bindTooltip(
                    `${zip}${territory ? ` · ${territory}` : ''}`,
                    { sticky: true }
                  );
                }}
              />
            );
          })}
          {dotItems.map((item) => {
            const style = STATUS_STYLE[normalizeStatus(item.status)];
            return (
              <CircleMarker
                key={`${item.status || 'selected'}-${item.zip}`}
                center={[item.latitude, item.longitude]}
                radius={4.5}
                pathOptions={{
                  color: style.color,
                  fillColor: style.color,
                  fillOpacity: 0.7,
                  weight: 1,
                }}
              >
                <Tooltip direction="top" offset={[0, -4]}>
                  <span>
                    {item.zip}
                    {item.territory_name ? ` · ${item.territory_name}` : ''}
                    {item.status === 'add' ? ' · added' : ''}
                    {item.status === 'remove' ? ' · removed' : ''}
                  </span>
                </Tooltip>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </Box>
      <Stack
        direction="row"
        spacing={2}
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 0.75 }}
      >
        {legendEntries.map((key) => (
          <Typography key={key} variant="caption" color="text.secondary">
            <LegendDot color={STATUS_STYLE[key].color} />
            {STATUS_STYLE[key].label} ({counts[key].toLocaleString()})
          </Typography>
        ))}
        {truncated && (
          <Typography variant="caption" color="warning.main">
            Large selection — only the first 6,000 ZIPs are plotted.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
